import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  DEAL_RESULT_DEFS,
  DEAL_RESULT_MAP,
  DEAL_STAGE_DEFS,
  DEAL_STAGE_MAP,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { useDebounced, useListState } from "../../lib/hooks";
import { date, money, plural, WORDS } from "../../lib/format";
import { Icon } from "../../components/Icon";
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from "../../components/ui";
import { RecordStats, type RecordStat } from "../../components/RecordStats";
import { CardPickerButton, useCardChoice } from "../../components/CardPicker";
import { OwnerChip } from "../../components/People";
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from "../../components/ColumnPicker";
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from "../../components/ColumnFilter";
import { DealDetail } from "./DealDetail";

interface Pipeline {
  stages: Array<{
    stage: string;
    label: string;
    short: string;
    color: string;
    probability: number;
    count: number;
    value: number;
    weightedValue: number;
  }>;
  open: { count: number; value: number; weightedValue: number };
  closed: {
    won: { count: number; value: number };
    lost: { count: number; value: number };
    winRate: number | null;
    averageDealValue: number;
    averageDaysToClose: number | null;
  };
  periodDays: number;
}

interface DealRow {
  id: string;
  title: string;
  stage: keyof typeof DEAL_STAGE_MAP;
  result: keyof typeof DEAL_RESULT_MAP;
  value: string;
  probability: number;
  expectedCloseAt: string | null;
  closedAt: string | null;
  ownerId: string | null;
  account: { id: string; name: string } | null;
  lead?: { id: string; name: string } | null;
  /** حالُ عروض هذه الصفقة — به يُعرف عمودُها في اللوحة. */
  quotations?: Array<{ id: string; status: string }>;
}

/**
 * كتالوج أعمدة الصفقات.
 *
 * «الصفقة» ثابتٌ لا يُخفى: هو ما يُعرف به الصفّ، وجدولٌ بلا عنوانٍ صفوفُه
 * أرقامٌ بلا أصحاب.
 */
const DEAL_COLUMNS: readonly ColumnDef[] = [
  { key: "title", label: "الصفقة", fixed: true },
  { key: "account", label: "الحساب" },
  { key: "stage", label: "المرحلة" },
  { key: "result", label: "النتيجة" },
  { key: "value", label: "القيمة", group: "الأرقام" },
  { key: "probability", label: "الاحتمالية", group: "الأرقام" },
  { key: "weighted", label: "القيمة المرجّحة", group: "الأرقام" },
  { key: "expectedCloseAt", label: "الإغلاق المتوقع", group: "التواريخ" },
  { key: "owner", label: "المسؤول" },
];

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في deals.service.ts. */
const DEAL_FILTERS: FilterDef[] = [
  { key: "title", label: "الصفقة", kind: "text" },
  { key: "account", label: "الحساب", kind: "text", noList: true },
  {
    key: "stage",
    label: "المرحلة",
    kind: "enum",
    sortable: true,
    options: DEAL_STAGE_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  {
    key: "result",
    label: "النتيجة",
    kind: "enum",
    options: DEAL_RESULT_DEFS.map((r) => ({ value: r.key, label: r.label })),
  },
  { key: "value", label: "القيمة", kind: "number", sortable: true },
  { key: "probability", label: "الاحتمالية", kind: "number" },
  {
    key: "expectedCloseAt",
    label: "الإغلاق المتوقع",
    kind: "date",
    sortable: true,
  },
  { key: "owner", label: "المسؤول", kind: "user", allowNone: true },
];

/* المرجّحة مخفيّةٌ ابتداءً: مشتقّةٌ من عمودين ظاهرين، فلا تُزاحمهما بلا طلب. */
const DEAL_COLUMNS_HIDDEN = ["weighted", "owner"];

/** أعمدةٌ رأسُها يُحاذى كخلاياها — الأرقام على اليمين. */
const NUMERIC_COLUMNS = new Set(["value", "probability", "weighted"]);

interface DealPage {
  items: DealRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export function DealsList() {
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [openDeal, setOpenDeal] = useState<string | null>(null);

  const columns = useMemo<ColumnDef[]>(() => [...DEAL_COLUMNS], []);
  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice("goyone.columns.deals", columns, DEAL_COLUMNS_HIDDEN);
  const colDrag = useColumnDrag(columnState, setColumnState);
  /*
   * جدولٌ أم لوحة؟ الاختيارُ يُحفَظ.
   *
   * الجدولُ للمسح: من يبحث عن صفقةٍ بعينها أو يفرز بالقيمة. واللوحةُ
   * للنظرة: أين تقف الصفقاتُ اليوم وأين ركدت. وهما سؤالان مختلفان
   * لشخصٍ واحد في وقتين، فلا يُلغى أحدهما بالآخر — ولا يُسأل عن اختياره
   * كلَّ مرّة.
   */
  const [view, setView] = useState<"table" | "board">(() => {
    try {
      return localStorage.getItem("goyone.view.deals") === "board"
        ? "board"
        : "table";
    } catch {
      return "table";
    }
  });
  const chooseView = (v: "table" | "board") => {
    setView(v);
    try {
      localStorage.setItem("goyone.view.deals", v);
    } catch {
      /* متصفّحٌ يمنع التخزين — الاختيار يعيش هذه الجلسة وحدها. */
    }
  };

  const [search, setSearch] = useState(state.q ?? "");
  const debounced = useDebounced(search);

  /*
   * اللوحةُ تطلب مئةً دفعةً واحدة.
   *
   * صفحةُ الجدول عشرون صفًّا وهو ما يُقرأ؛ ولوحةٌ من عشرين بطاقةً موزّعةً
   * على ستّة أعمدةٍ لا تُري توزّعًا. والحدُّ مئةٌ يحكمه الخادم — فإن جاوزت
   * الصفقاتُ ذلك قيل صراحةً كم يُعرض منها، ولا يُترك الرقمُ يوهم.
   */
  const query = {
    ...state,
    q: debounced || undefined,
    page,
    ...(view === "board" ? { page: "1", pageSize: "100" } : {}),
  };

  const list = useQuery({
    queryKey: ["deals", query],
    queryFn: () => api<DealPage>(`/sales/deals${qs(query)}`),
  });

  /* القمع والأرقام: مصدرٌ واحد للبطاقات والتحليل الجانبي. */
  const pipeline = useQuery({
    queryKey: ["deals", "pipeline"],
    queryFn: () => api<Pipeline>("/sales/deals/pipeline"),
  });

  const openValue = (list.data?.items ?? [])
    .filter((d) => d.result === "OPEN")
    .reduce((sum, d) => sum + Number(d.value), 0);

  const cardChoice = useCardChoice("goyone.stats.deals", [
    "open",
    "openValue",
    "weighted",
    "winRate",
    "avgValue",
  ]);
  const statItems = pipeline.data ? dealCards(pipeline.data) : [];
  const statChoices = statItems.map(({ key, label, group }) => ({
    key,
    label,
    group,
  }));

  return (
    /*
      الصفحةُ تمرّ في الجدول، وتثبت في اللوحة.

      اللوحةُ تملأ الشاشةَ ولا تمرّ: رأسُها ثابتٌ وأعمدتُها تمرّ في نفسها،
      فتحتاج `page-fill` ليُقاس ارتفاعُها على ما بقي. أمّا الجدولُ فيريد
      عكسَ ذلك: أن تصعد البطاقاتُ والعنوانُ حتى يبلغ رأسُه أعلى الشاشة
      فيلتصق ويُفسح للصفوف — كجدول العملاء سواء. وهما لا يجتمعان في صنفٍ
      واحد، فيُختار بحسب المعروض.
    */
    <div
      className={
        view === "board" ? "page page-fill is-board" : "page"
      }
    >
      {/*
        العنوانُ سطرٌ في وضع الجدول، وجزءٌ من الشريط في وضع اللوحة.

        سطرُ العنوان يشغل عرضَ الشاشة كلَّه ولا يحمل إلا كلمةً واحدةً في
        طرفه — والباقي بياضٌ. والجدولُ يمرّ فذلك البياضُ يذهب مع أوّل
        دفعة؛ أمّا اللوحةُ فلا تمرّ، فيبقى مقتطعًا من البطاقات إلى آخر
        الجلسة. فيُضمّ العنوانُ إلى الشريط ويُملأ السطرُ بما يُستعمل.
      */}
      {view === "table" && (
      <PageHead
        title="الصفقات"
        subtitle={
          /*
            العنوانُ الفرعيّ للجدول وحده.

            يقول ما تقوله بطاقتا «قيد العمل» و«متوسط قيمة الصفقة» فوق
            اللوحة — سطرٌ مكرَّرٌ يأكل من ارتفاعٍ لا يُعوَّض في شاشةٍ
            قصيرة، وما يُقال مرّتين يُقرأ صفرًا.
          */
          view === "table" && list.data
            ? `${plural(list.data.total, WORDS.deal)} — قيمة المفتوح في هذه الصفحة ${money(openValue)}`
            : undefined
        }
      />
      )}

      {/*
        بطاقاتُ الصفحة للجدول وحده.

        اللوحةُ تحمل أرقامَها بنفسها وهي تصف ما يُرى فيها؛ وبطاقاتُ الصفحة
        تصف القمعَ كلَّه. وعرضُهما معًا يضع رقمين لاسمٍ واحد على شاشةٍ
        واحدة — «معدّل الفوز» مرّتين بمقامين — فيُصدَّق أيّهما؟ فلكلّ شكلٍ
        مصدرُه، ولا يُجمع بينهما.
      */}
      {view === "table" && pipeline.data && (
        <RecordStats
          items={statItems}
          storageKey="goyone.stats.deals"
          defaults={["open", "openValue", "weighted", "winRate", "avgValue"]}
          external={cardChoice}
        />
      )}

      <div className="toolbar sticky-bar">
        {view === "board" && <h1 className="page-title db-title">الصفقات</h1>}
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بعنوان الصفقة"
        />

        <select
          className="select"
          style={{ width: "auto" }}
          value={state.stage ?? ""}
          onChange={(e) => patch({ stage: e.target.value || undefined })}
          aria-label="المرحلة"
        >
          <option value="">كل المراحل</option>
          {DEAL_STAGE_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className="select"
          style={{ width: "auto" }}
          value={state.result ?? ""}
          onChange={(e) => patch({ result: e.target.value || undefined })}
          aria-label="النتيجة"
        >
          <option value="">كل النتائج</option>
          <option value="OPEN">قيد العمل</option>
          <option value="WON">ناجحة</option>
          <option value="LOST">خاسرة</option>
        </select>

        {/* أزرار التحكم في شكل الصفحة — الأعمدة والبطاقات والقمع وحجم الجدول. */}
        <span
          className="row"
          style={{ marginInlineStart: "auto", gap: "var(--sp-2)" }}
        >
          {/* التبديلُ أوّلًا: هو الذي يقرّر ما تعنيه بقيّةُ الأزرار. */}
          <span className="seg" role="group" aria-label="شكل العرض">
            <button
              type="button"
              className={`seg-btn${view === "table" ? " is-on" : ""}`}
              aria-pressed={view === "table"}
              onClick={() => chooseView("table")}
              title="جدولٌ يُفرز ويُصفَّى"
            >
              <Icon name="listUl" size={13} />
              جدول
            </button>
            <button
              type="button"
              className={`seg-btn${view === "board" ? " is-on" : ""}`}
              aria-pressed={view === "board"}
              onClick={() => chooseView("board")}
              title="لوحةٌ تُري أين تقف الصفقات"
            >
              <Icon name="chart" size={13} />
              لوحة
            </button>
          </span>

          {/* الفلتر للشكلين معًا: اللوحة تُقرأ من القائمة نفسها. */}
          <FilterButton
            defs={DEAL_FILTERS}
            filters={filters}
            onChange={setFilters}
          />
          {view === "table" && (
            <ColumnPickerButton
              columns={columns}
              state={columnState}
              onChange={setColumnState}
              onReset={resetColumns}
              touched={columnsTouched}
            />
          )}
          {view === "table" && (
            <CardPickerButton
              choices={statChoices}
              chosen={cardChoice.chosen}
              onChange={cardChoice.setChosen}
              onReset={cardChoice.reset}
            />
          )}
        </span>
      </div>

      <FilterChips
        defs={DEAL_FILTERS}
        filters={filters}
        onChange={setFilters}
      />

      {view === "board" ? (
        <div className="card fill-rest db-wrap">
          {list.isError && (
            <ErrorState
              error={list.error}
              onRetry={() => void list.refetch()}
            />
          )}
          {list.data && (
            <DealsBoard
              deals={list.data.items}
              total={list.data.total}
              onOpen={setOpenDeal}
            />
          )}
        </div>
      ) : (
      <FilterScope
        defs={DEAL_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/sales/deals/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {shownColumns.map((c) => (
                  <th
                    key={c.key}
                    {...colDrag.thProps(
                      c,
                      NUMERIC_COLUMNS.has(c.key) ? "col-num" : undefined,
                    )}
                  >
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length} />}

            {list.data && (
              <tbody>
                {list.data.items.map((d) => (
                  <tr
                    key={d.id}
                    className="clickable"
                    onClick={() => setOpenDeal(d.id)}
                  >
                    {shownColumns.map((c) => (
                      <DealCell key={c.key} column={c} deal={d} />
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        )}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد صفقات"
            text="تُنشأ الصفقة عند تحويل عميل محتمل إلى حساب."
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>
      )}

      {/* القمع موضعه شاشته: /sales/pipeline — وهنا جدولٌ يُقرأ ويُصفّى. */}

      {openDeal && (
        <DealDetail id={openDeal} onClose={() => setOpenDeal(null)} />
      )}
    </div>
  );
}

/* ==========================================================================
   لوحة الصفقات — ستّةُ أعمدةٍ ترسم الرحلة، وفوقها ما يُقرأ بنظرة
   ========================================================================== */

type DealLane = "NEW" | "SENT" | "WON" | "LOST";

/**
 * أعمدةُ اللوحة.
 *
 * الجدولُ يقول ما لكلّ صفقةٍ من مرحلةٍ ونتيجةٍ وقيمة، ولا يقول أين يقف
 * العملُ. وأربعةٌ تكفي لقوله: ما لم يخرج بعدُ، وما خرج ينتظر، وما رُبح،
 * وما خُسر.
 *
 * وما بين الخروج والحسم — متابعةٌ وموافقةٌ — كان عمودين فصار داخل
 * «أُرسلت»: كلُّه عرضٌ عند العميل ينتظر أن يُغلق، ولا يُترك عمودٌ يُحذف
 * فتختفي صفقاتُه من اللوحة وهي قائمة.
 *
 * والألوان: المفتوحان يتجاوران في شريط التوزّع فتُفحص بينهما فروقُ عمى
 * الألوان؛ والفائزةُ والخاسرة لونا حالةٍ محجوزان لا يقعان في الشريط.
 */
const DEAL_LANES: Array<{
  key: DealLane;
  label: string;
  tone: string;
  hint: string;
  /** أمالٌ ما زال في الطريق؟ هذه وحدها تدخل شريط التوزّع. */
  open: boolean;
}> = [
  {
    key: "NEW",
    label: "جديدة",
    tone: "var(--mv-2)",
    hint: "لم يخرج لها عرضٌ مُرسَل بعد.",
    open: true,
  },
  {
    key: "SENT",
    label: "أُرسلت",
    tone: "var(--brand)",
    hint: "خرج عرضُها إلى العميل — تنتظر ردًّا أو متابعةً أو موافقة.",
    open: true,
  },
  {
    key: "WON",
    label: "فائزة",
    tone: "var(--success)",
    hint: "أُغلقت رابحةً.",
    open: false,
  },
  {
    key: "LOST",
    label: "خاسرة",
    tone: "var(--danger)",
    hint: "أُغلقت خاسرةً.",
    open: false,
  },
];

/**
 * عمودُ الصفقة من مرحلتها — حين لا عرضَ لها.
 *
 * ليست كلُّ صفقةٍ يخرج لها عرضُ سعرٍ مسجَّل: منها ما يُتفاوض عليه شفاهًا
 * ويُسجَّل بمرحلته وحدها. فلو قُصرت اللوحةُ على العروض لوقف أكثرُها في
 * «جديدة» وهو في التفاوض فعلًا — لوحةٌ تكذب أهونُ من لا لوحة؟ لا.
 * فالمرحلةُ تُقرأ حين يغيب العرض، والعرضُ يقدَّم عليها حين يحضر: العرضُ
 * واقعةٌ لها أثرٌ في النظام، والمرحلةُ حكمُ صاحبها.
 */
const STAGE_LANE: Record<string, DealLane> = {
  CONTACT: "NEW",
  QUALIFICATION: "NEW",
  PROPOSAL: "SENT",
  NEGOTIATION: "SENT",
  CLOSING: "SENT",
};

/** أبعدُ ما بلغه عرضٌ من عروض الصفقة — فالأبعدُ هو موضعُ العمل. */
const QUOTE_RANK: Record<string, number> = {
  ACCEPTED: 4,
  NEGOTIATION: 3,
  SENT: 2,
  DRAFT: 1,
  REJECTED: 0,
  EXPIRED: 0,
};

/**
 * أين تقف هذه الصفقة؟
 *
 * النتيجةُ تحسم أوّلًا: ما أُغلق أُغلق، ولا يُسأل عن عرضه بعدها. وما بقي
 * مفتوحًا يُسأل عن أبعد عروضه — فصفقةٌ لها عرضٌ مرفوضٌ وآخرُ يُتابَع تقف
 * عند المتابعة لا عند الرفض. ومن لا عرضَ خرج له، أو عرضُه مسودّةٌ أو
 * ساقط، فمرحلتُه هي الشاهد.
 */
function laneOfDeal(d: DealRow): DealLane {
  if (d.result === "WON") return "WON";
  if (d.result === "LOST") return "LOST";
  const best = (d.quotations ?? []).reduce(
    (top, q) => Math.max(top, QUOTE_RANK[q.status] ?? 0),
    0,
  );
  if (best >= 2) return "SENT";
  return STAGE_LANE[d.stage] ?? "NEW";
}

function DealsBoard({
  deals,
  total,
  onOpen,
}: {
  deals: DealRow[];
  total: number;
  onOpen: (id: string) => void;
}) {
  const lanes = DEAL_LANES.map((lane) => {
    const items = deals.filter((d) => laneOfDeal(d) === lane.key);
    return {
      ...lane,
      items,
      sum: items.reduce((n, d) => n + Number(d.value ?? 0), 0),
    };
  });

  const openLanes = lanes.filter((l) => l.open);
  const openSum = openLanes.reduce((n, l) => n + l.sum, 0);
  const openCount = openLanes.reduce((n, l) => n + l.items.length, 0);
  const won = lanes.find((l) => l.key === "WON")!;
  const lost = lanes.find((l) => l.key === "LOST")!;
  const decided = won.items.length + lost.items.length;
  const winRate = decided
    ? Math.round((won.items.length / decided) * 100)
    : null;
  const avg = deals.length
    ? deals.reduce((n, d) => n + Number(d.value ?? 0), 0) / deals.length
    : 0;

  if (deals.length === 0) {
    return (
      <EmptyState
        title="لا توجد صفقات"
        text="تُنشأ الصفقة عند تحويل عميل محتمل إلى حساب."
      />
    );
  }

  return (
    <div className="db-board">
      {/*
        أربعةُ أرقامٍ تسبق الأعمدة.

        اللوحةُ تقول «ماذا يوجد»، ولا تقول «كيف نحن». والأسئلة أربعة: كم
        مالٌ ما زال في الطريق، وكم كسبنا، وكم نكسب ممّا نحسم — من المحسوم
        وحده، وإلا بدا بائعٌ نشيطٌ فاشلًا لأنّ صفقاتِه مفتوحة — وكم تساوي
        الصفقةُ الواحدة. وكلٌّ منها بجواره مقامُه، فرقمٌ بلا مقامه دعوى.
      */}
      <div className="db-kpis">
        <div className="db-kpi">
          <span className="db-kpi-label">قيد العمل</span>
          <span className="db-kpi-value num">{money(openSum)}</span>
          <span className="db-kpi-hint">
            {openCount
              ? `${plural(openCount, WORDS.deal)} لم تُغلق`
              : "لا شيء مفتوح"}
          </span>
        </div>

        <div className="db-kpi">
          <span className="db-kpi-label">فائزة</span>
          <span className="db-kpi-value num" style={{ color: "var(--success)" }}>
            {money(won.sum)}
          </span>
          <span className="db-kpi-hint">
            {won.items.length
              ? `${plural(won.items.length, WORDS.deal)} رابحة`
              : "لا فوز في المعروض"}
          </span>
        </div>

        {/* حلقةٌ تقول النسبةَ قبل أن تُقرأ: أخضرُ بقدر الفوز وأحمرُ باقيه. */}
        <div className="db-kpi is-ring">
          <span
            className="db-ring"
            style={{ ["--db-pct" as string]: `${winRate ?? 0}%` }}
            role="img"
            aria-label={
              winRate === null
                ? "لا صفقة محسومة في المعروض"
                : `معدّل الفوز ${winRate} بالمئة`
            }
          >
            <span className="db-ring-num num">
              {winRate === null ? "—" : `${winRate}%`}
            </span>
          </span>
          <span className="db-kpi-side">
            <span className="db-kpi-label">معدّل الفوز</span>
            <span className="db-kpi-hint">
              {decided
                ? `من ${plural(decided, WORDS.deal)} محسومة`
                : "لا صفقة محسومة هنا"}
            </span>
          </span>
        </div>

        <div className="db-kpi">
          <span className="db-kpi-label">متوسط قيمة الصفقة</span>
          <span className="db-kpi-value num">{money(avg)}</span>
          <span className="db-kpi-hint">
            على {plural(deals.length, WORDS.deal)}
            {total > deals.length && ` من ${total}`}
          </span>
        </div>
      </div>

      {/* توزّعُ المال المفتوح على مراحله — يُرى الركودُ حيث هو. */}
      {openSum > 0 ? (
        <div className="db-flow">
          <div className="db-flow-bar">
            {openLanes
              .filter((l) => l.sum > 0)
              .map((l) => (
                <span
                  key={l.key}
                  className="db-flow-seg"
                  style={{
                    width: `${(l.sum / openSum) * 100}%`,
                    ["--db-tone" as string]: l.tone,
                  }}
                  title={`${l.label} — ${money(l.sum)} · ${plural(l.items.length, WORDS.deal)}`}
                />
              ))}
          </div>
          {/*
            الدليلُ يحمل الاسمَ والنسبةَ والمبلغ بحبرٍ على خلفيّته. وكتابةُ
            النسبة داخل القطعة تبدو أنيقةً وتُقرأ بصعوبة: الأبيضُ على
            العنبر والفيروزيّ دون حدّ التباين — فمكانُها هنا.
          */}
          <div className="db-flow-legend">
            {openLanes
              .filter((l) => l.sum > 0)
              .map((l) => (
                <span key={l.key} className="db-flow-key">
                  <span
                    className="db-dot"
                    style={{ ["--db-tone" as string]: l.tone }}
                  />
                  {l.label}
                  <span className="num db-flow-pct">
                    {Math.round((l.sum / openSum) * 100)}%
                  </span>
                  <span className="num tiny mute">{money(l.sum)}</span>
                </span>
              ))}
          </div>
        </div>
      ) : (
        <p className="tiny mute">لا مال مفتوحٌ في المعروض — كلُّه محسوم.</p>
      )}

      {/* ما زاد على المئة لا يُخفى: الرقمُ يُقال حتى لا يُقرأ الجزءُ كلًّا. */}
      {total > deals.length && (
        <p className="tiny mute">
          اللوحة تعرض {plural(deals.length, WORDS.deal)} من {total} — صفِّ
          بالمرحلة أو المالك أو ابحث لتضييق المعروض، أو انتقل إلى الجدول
          لتصفّح الباقي.
        </p>
      )}

      <div className="db-grid">
        {lanes.map((lane) => (
          <div
            key={lane.key}
            className="db-col"
            style={{ ["--db-tone" as string]: lane.tone }}
          >
            <div className="db-col-head" title={lane.hint}>
              {lane.label}
              <span className="db-count num">{lane.items.length}</span>
              <span className="num tiny">{money(lane.sum)}</span>
            </div>

            {lane.items.length === 0 && (
              <p className="tiny mute center">لا شيء هنا.</p>
            )}

            <div className="db-cards">
              {lane.items.map((d, i) => (
                /*
                 * البطاقةُ تفتح الصفقةَ في لوحتها الجانبيّة — كصفّ الجدول
                 * سواء، فلا يتعلّم من ينتقل بين الشكلين طريقتين.
                 */
                <div
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  className="db-card"
                  style={{ ["--db-i" as string]: Math.min(i, 6) }}
                  onClick={() => onOpen(d.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onOpen(d.id);
                  }}
                  title="افتح الصفقة"
                >
                  <span className="db-card-title">{d.title}</span>
                  {(d.account || d.lead) && (
                    <span className="tiny mute">
                      {d.account?.name ?? d.lead?.name}
                      {!d.account && d.lead && " · محتمل"}
                    </span>
                  )}
                  <span className="num db-card-value">{money(d.value)}</span>
                  <span className="db-card-foot">
                    <span className="db-stage">
                      {DEAL_STAGE_MAP[d.stage]?.short ?? ""}
                    </span>
                    {d.expectedCloseAt && (
                      <span className="tiny mute nowrap">
                        {date(d.expectedCloseAt)}
                      </span>
                    )}
                    {/*
                      المسؤولُ صورةٌ لا اسمًا: العمودُ مئتا بكسلٍ يتقاسمها
                      المرحلةُ والموعدُ والاسم، فيقصّ الاسمُ فلا يُقرأ ولا
                      يُفسح. والصورةُ تُعرَف بلمحةٍ واسمُها في تلميحها.
                    */}
                    <OwnerChip ownerId={d.ownerId} size={18} showName={false} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * خليّةٌ واحدة، يُختار شكلها بمفتاح العمود لا بموضعه.
 *
 * الأعمدة تُخفى وتُرتَّب، فخليّةٌ تعرف نفسها بترتيبها تنكسر عند أول إخفاء.
 */
function DealCell({ column, deal }: { column: ColumnDef; deal: DealRow }) {
  switch (column.key) {
    case "title":
      return <td className="cell-strong">{deal.title}</td>;

    case "account":
      return (
        <td>
          {deal.account ? (
            /* الرابط لا يفتح الصفّ: وجهتاه مختلفتان. */
            <Link
              to={`/crm/accounts/${deal.account.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {deal.account.name}
            </Link>
          ) : deal.lead ? (
            <Link
              to={`/crm/leads/${deal.lead.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {deal.lead.name} <span className="tiny mute">محتمل</span>
            </Link>
          ) : (
            "—"
          )}
        </td>
      );

    case "stage":
      return (
        <td className="cell-mute">
          {DEAL_STAGE_MAP[deal.stage]?.short ?? deal.stage}
        </td>
      );

    case "result":
      return (
        <td>
          <Badge kind={DEAL_RESULT_MAP[deal.result]?.kind}>
            {DEAL_RESULT_MAP[deal.result]?.label}
          </Badge>
        </td>
      );

    case "value":
      return <td className="num">{money(deal.value)}</td>;

    case "probability":
      return <td className="num cell-mute">{deal.probability}٪</td>;

    case "weighted":
      /* ما يُتوقع تحصيله من هذه الصفقة وحدها: قيمتها في احتماليتها. */
      return (
        <td className="num cell-mute">
          {money((Number(deal.value) * deal.probability) / 100)}
        </td>
      );

    case "expectedCloseAt":
      return (
        <td className="cell-mute">
          {deal.closedAt
            ? `أُغلقت ${date(deal.closedAt)}`
            : date(deal.expectedCloseAt)}
        </td>
      );

    case "owner":
      return (
        <td>
          <OwnerChip ownerId={deal.ownerId} size={22} />
        </td>
      );

    default:
      return <td>—</td>;
  }
}

/**
 * بطاقات الصفقات.
 *
 * من القمع لا من الصفحة المعروضة: البطاقات تصف خطّ المبيعات كلّه، لا
 * العشرين صفقةً التي تصادف ظهورها. والقيمة المرجّحة — ما يُتوقع تحصيله
 * فعلًا — أصدق من القيمة الخام التي تفترض نجاح كل صفقة.
 */
function dealCards(p: Pipeline): RecordStat[] {
  const c = p.closed;
  return [
    {
      key: "open",
      label: "صفقات مفتوحة",
      group: "المفتوح",
      icon: "funnel",
      color: "#3672EA",
      value: p.open.count,
      numeric: true,
      hint: "ما زال قيد التفاوض.",
    },
    {
      key: "openValue",
      label: "قيمة المفتوح",
      group: "المفتوح",
      icon: "wallet",
      color: "#1D46A6",
      value: money(p.open.value),
      numeric: true,
      hint: "مجموع قيم الصفقات المفتوحة الخام.",
    },
    {
      key: "weighted",
      label: "المتوقّع تحصيله",
      group: "المفتوح",
      icon: "chartBar",
      color: "#51A521",
      value: money(p.open.weightedValue),
      numeric: true,
      note: "مرجَّحٌ باحتمالية كل مرحلة",
      hint: "قيمة المفتوح مضروبةً في احتمالية مرحلتها.",
    },
    {
      key: "won",
      label: `ناجحة (${p.periodDays} يومًا)`,
      group: "المُغلق",
      icon: "check",
      color: "#51A521",
      value: c.won.count,
      numeric: true,
      note: money(c.won.value),
    },
    {
      key: "lost",
      label: `خاسرة (${p.periodDays} يومًا)`,
      group: "المُغلق",
      icon: "alert",
      color: "#DC2626",
      value: c.lost.count,
      numeric: true,
      note: money(c.lost.value),
    },
    {
      key: "winRate",
      label: "نسبة الفوز",
      group: "الأداء",
      icon: "zap",
      color: c.winRate != null && c.winRate >= 50 ? "#51A521" : "#BF930B",
      value: c.winRate != null ? `${c.winRate}٪` : "—",
      numeric: true,
      hint: "الناجحة من مجموع المُغلق.",
    },
    {
      key: "avgValue",
      label: "متوسط قيمة الصفقة",
      group: "الأداء",
      icon: "receipt",
      color: "#8791A1",
      value: money(c.averageDealValue),
      numeric: true,
    },
    {
      key: "avgDays",
      label: "متوسط مدة الإغلاق",
      group: "الأداء",
      icon: "clock",
      color: "#5596FF",
      value:
        c.averageDaysToClose != null ? `${c.averageDaysToClose} يومًا` : "—",
      numeric: true,
      note: "على الرابحة وحدها",
    },
  ];
}
